import { Avatar, Box, Sheet, Typography } from '@mui/joy';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';

import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import Diversity1OutlinedIcon from '@mui/icons-material/Diversity1Outlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

import { closeSidebar } from '../../utils';
import { LayoutContext } from '../../context';

const TABS = [
    { path: '/match', label: 'Симпатии', Icon: FavoriteBorderRoundedIcon },
    { path: '/lenta', label: 'Лента', Icon: StyleOutlinedIcon },
    { path: '/quizzes', label: 'Тесты', Icon: QuizOutlinedIcon },
    { path: '/chats', label: 'Чаты', Icon: ChatBubbleOutlineRoundedIcon },
    { path: '/unions', label: 'Союзы', Icon: Diversity1OutlinedIcon },
];

/** Панель занимает собственное место в flex-layout: контент не прячется под ней. */
export default function BottomMenu() {
    const { modalQuestionnaire, profileUser, setModalQuestionnaire } = useContext(LayoutContext);
    const navigate = useNavigate();
    const location = useLocation();
    const reduceMotion = useReducedMotion();
    const profileOpen = Boolean(modalQuestionnaire?.open);
    const spring = reduceMotion
        ? { duration: 0 }
        : { type: 'spring', stiffness: 430, damping: 34 };

    const tabs = [
        ...TABS,
        { path: 'profile', label: 'Анкета', Icon: PersonOutlineRoundedIcon },
    ];

    const selectTab = (path) => {
        if (path === 'profile') {
            setModalQuestionnaire((prev) => ({ ...prev, open: true }));
        } else {
            navigate(path, { replace: true });
        }
        closeSidebar();
    };

    return (
        <Box sx={{
            display: { xs: 'block', md: 'none' }, flex: 'none',
            position: 'relative', zIndex: 30,
            px: 1, pt: 1, pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
            background: 'linear-gradient(180deg, rgba(249, 245, 255, 0), rgba(249, 245, 255, .96) 35%)',
        }}>
            <LayoutGroup id="lovirds-bottom-navigation">
                <Sheet
                    component="nav"
                    aria-label="Основные разделы"
                    sx={{
                        display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                        alignItems: 'stretch', gap: 0,
                        maxWidth: 560, mx: 'auto', px: 0.5, py: 0.75,
                        border: '1px solid rgba(174, 144, 206, .13)',
                        borderRadius: '26px',
                        backgroundColor: 'rgba(255, 255, 255, .96)',
                        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                        boxShadow: '0 8px 28px rgba(94, 60, 140, .09), inset 0 1px 0 rgba(255,255,255,.9)',
                    }}
                >
                    {tabs.map(({ path, label, Icon }) => {
                        const profile = path === 'profile';
                        const routeActive = location.pathname === path || location.pathname.startsWith(`${path}/`);
                        const active = profile ? profileOpen : !profileOpen && routeActive;
                        return (
                            <Box
                                key={path}
                                component={motion.button}
                                type="button"
                                onClick={() => selectTab(path)}
                                aria-label={profile ? 'Открыть мою анкету' : label}
                                aria-current={!profile && routeActive ? 'page' : undefined}
                                aria-haspopup={profile ? 'dialog' : undefined}
                                aria-expanded={profile ? profileOpen : undefined}
                                whileTap={reduceMotion ? undefined : { scale: 0.96 }}
                                sx={{
                                    position: 'relative', isolation: 'isolate', display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    justifyContent: 'center', gap: 0.625, minWidth: 0, minHeight: 62,
                                    border: 0, p: 0.25, borderRadius: '18px',
                                    background: 'none', cursor: 'pointer', font: 'inherit',
                                    color: active ? '#8750c3' : '#806c8d',
                                    transition: reduceMotion ? 'none' : 'color .18s', WebkitTapHighlightColor: 'transparent',
                                    '&:focus-visible': { outline: '2px solid #a855f7', outlineOffset: '-2px' },
                                }}
                            >
                                {active && (
                                    <Box component={motion.span}
                                        layoutId={reduceMotion ? undefined : 'active-tab-surface'}
                                        transition={spring}
                                        sx={{ position: 'absolute', inset: '0 1px', zIndex: -1, borderRadius: '18px', background: 'linear-gradient(180deg, #faf3ff, rgba(248,243,252,.55))' }}
                                    />
                                )}
                                <Box sx={{
                                    position: 'relative', display: 'grid', placeItems: 'center',
                                    width: 'clamp(34px, 9vw, 42px)', height: 34,
                                }}>
                                    {active && (
                                        <Box
                                            component={motion.span}
                                            layoutId={reduceMotion ? undefined : 'active-tab-icon'}
                                            transition={spring}
                                            sx={{
                                                position: 'absolute', inset: 0, borderRadius: '13px',
                                                background: 'var(--lvc-grad)',
                                                boxShadow: '0 4px 12px rgba(170, 107, 208, .18), inset 0 1px 0 rgba(255,255,255,.22)',
                                            }}
                                        />
                                    )}
                                    <Box
                                        component={motion.span}
                                        animate={{ y: active && !reduceMotion ? -1 : 0 }}
                                        transition={spring}
                                        sx={{
                                            position: 'relative', display: 'grid', placeItems: 'center',
                                            color: active ? '#fff' : 'inherit',
                                        }}
                                    >
                                        {profile && profileUser?.photo_file_path ? (
                                            <Avatar
                                                alt=""
                                                src={profileUser.photo_file_path}
                                                sx={{
                                                    '--Avatar-size': '25px', borderRadius: '10px', color: active ? '#fff' : '#9b83a8', backgroundColor: 'transparent',
                                                    outline: active ? '1.5px solid rgba(255,255,255,.8)' : 'none',
                                                }}
                                            >
                                                <PersonOutlineRoundedIcon sx={{ fontSize: 21, color: active ? '#fff' : '#9b83a8' }} />
                                            </Avatar>
                                        ) : <Icon sx={{ fontSize: 23, color: active ? '#fff' : '#9b83a8' }} />}
                                    </Box>
                                </Box>
                                <Typography sx={{
                                    color: 'inherit', fontSize: 'clamp(9px, 2.65vw, 10.5px)',
                                    lineHeight: 1.15, fontWeight: active ? 700 : 500,
                                    whiteSpace: 'nowrap', letterSpacing: '-0.015em',
                                }}>
                                    {label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Sheet>
            </LayoutGroup>
        </Box>
    );
}
